import { useState, useEffect, useRef } from "react";
import { ChevronDown, Globe } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const RED = "hsl(0,68%,32%)";
const SERIF = "'Cormorant Garamond', serif";

const LANGUAGES = [
  { code: "sq", name: "Shqip",      flag: "🇦🇱", label: "Albanian" },
  { code: "en", name: "English",    flag: "🇬🇧", label: "English" },
  { code: "it", name: "Italiano",   flag: "🇮🇹", label: "Italian" },
  { code: "el", name: "Ελληνικά",  flag: "🇬🇷", label: "Greek" },
  { code: "de", name: "Deutsch",    flag: "🇩🇪", label: "German" },
  { code: "sv", name: "Svenska",    flag: "🇸🇪", label: "Swedish" },
  { code: "no", name: "Norsk",      flag: "🇳🇴", label: "Norwegian" },
  { code: "da", name: "Dansk",      flag: "🇩🇰", label: "Danish" },
  { code: "zh", name: "中文",       flag: "🇨🇳", label: "Chinese" },
  { code: "nl", name: "Nederlands", flag: "🇳🇱", label: "Dutch" },
  { code: "pl", name: "Polski",     flag: "🇵🇱", label: "Polish" },
  { code: "cs", name: "Čeština",    flag: "🇨🇿", label: "Czech" },
];

// Food photos - mix of PDF crops and custom uploaded images
const FOOD_PHOTOS = {
  "cat-01": "/food_photos/cat_01.jpg",   // Antipasti
  "cat-02": "/food_photos/cat_01.jpg",   // Insalata
  "cat-03": "/food_photos/cat_03.jpg",   // Pasta
  "cat-04": "/food_photos/cat_04.jpg",   // Pizza
  "cat-05": "/food_photos/calzone.jpg",  // Calzone - custom photo
  "cat-06": "/food_photos/cat_07.jpg",   // Risotto
  "cat-07": "/food_photos/contorni.jpg", // Contorni - custom photo
  "cat-08": "/food_photos/zuppe.jpg",    // Zuppe - custom photo
  "cat-09": "/food_photos/cat_08.jpg",   // Pollo
  "cat-10": "/food_photos/cat_09.jpg",   // Carni
  "cat-11": "/food_photos/cat_10.jpg",   // Vitello
  "cat-12": "/food_photos/cat_09.jpg",   // Agnello & Maiale
  "cat-13": "/food_photos/cat_11.jpg",   // Pesce
  "cat-14": "/food_photos/texmex.jpg",   // Tex-Mex - custom photo
  "cat-15": "/food_photos/cat_12.jpg",   // Dessert
  "cat-16": "/food_photos/caffe.jpg",    // Caffè - custom photo
};

export default function MenuPage() {
  const [allData, setAllData] = useState({});
  const [lang, setLang] = useState(LANGUAGES[2]); // Italian default
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);
  const sectionRefs = useRef({});
  const navRef = useRef(null);

  // Load all translations once from static JSON
  useEffect(() => {
    fetch("/menu_data.json")
      .then(r => r.json())
      .then(data => {
        setAllData(data);
        const first = data[lang.code];
        if (first && first.length > 0) setActiveSection(first[0].category.id);
        setLoading(false);
      });
  }, []);

  const menu = allData[lang.code] || [];

  // Update active section when language changes
  useEffect(() => {
    if (menu.length > 0 && !activeSection) setActiveSection(menu[0].category.id);
  }, [lang]);

  // Highlight active nav item on scroll
  useEffect(() => {
    if (!menu.length) return;
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.dataset.catid); }),
      { rootMargin: "-30% 0px -65% 0px" }
    );
    Object.values(sectionRefs.current).forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, [menu]);

  const scrollTo = (catId) => {
    const el = sectionRefs.current[catId];
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 108;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
    setTimeout(() => {
      navRef.current?.querySelector(`[data-navid="${catId}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }, 80);
  };

  return (
    <div style={{ fontFamily: "'Manrope', sans-serif", background: "hsl(40,33%,98%)", minHeight: "100vh" }}>

      {/* ── STICKY HEADER ── */}
      <header className="sticky top-0 z-50 border-b shadow-sm"
        style={{ backdropFilter: "blur(18px)", background: "hsla(40,33%,98%,0.94)" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-10 flex items-center justify-between h-14">
          <div>
            <div style={{ fontFamily: SERIF, color: RED, fontSize: "1.25rem", fontWeight: 600, letterSpacing: "0.07em" }}>
              BIANCO E ROSSO
            </div>
            <div className="text-xs text-muted-foreground uppercase" style={{ letterSpacing: "0.15em" }}>
              Tsilivi · Zakynthos
            </div>
          </div>

          {/* Language Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="language-selector"
              className="flex items-center gap-2 px-3 py-2 rounded-full border hover:bg-secondary transition-colors text-sm font-medium">
              <span className="text-base">{lang.flag}</span>
              <span className="hidden sm:inline">{lang.name}</span>
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 max-h-80 overflow-y-auto">
              {LANGUAGES.map(l => (
                <DropdownMenuItem key={l.code} data-testid={`lang-${l.code}`}
                  onClick={() => setLang(l)}
                  className={`flex items-center gap-3 cursor-pointer ${l.code === lang.code ? "bg-secondary font-semibold" : ""}`}>
                  <span className="text-xl">{l.flag}</span>
                  <div>
                    <div className="text-sm font-medium">{l.name}</div>
                    <div className="text-xs text-muted-foreground">{l.label}</div>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* ── HERO (PDF Cover - NO text overlay on photo) ── */}
      <section className="relative overflow-hidden" style={{ height: "58vh", minHeight: "300px" }}>
        <img src="/menu_cover.png" alt="Bianco e Rosso"
          className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-8 pt-20"
          style={{ background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)" }}>
          <div className="max-w-4xl mx-auto text-white text-center">
            <p className="text-xs tracking-[0.3em] uppercase mb-2 opacity-80">Ristorante & Pizzeria</p>
            <h1 style={{ fontFamily: SERIF, fontSize: "clamp(2.5rem,8vw,5rem)", fontWeight: 300, fontStyle: "italic", lineHeight: 1 }}>
              Bianco e Rosso
            </h1>
            <p className="text-xs sm:text-sm tracking-[0.2em] uppercase mt-2 opacity-80">
              Tsilivi, Zakynthos, Greece
            </p>
          </div>
        </div>
      </section>

      {/* ── STICKY CATEGORY NAV ── */}
      <nav ref={navRef} className="sticky border-b bg-white/90 shadow-sm"
        style={{ top: "56px", zIndex: 40, backdropFilter: "blur(10px)" }}>
        <div className="tabs-scroll overflow-x-auto">
          <div className="flex min-w-max px-4">
            {menu.map(({ category: cat }) => (
              <button key={cat.id} data-navid={cat.id}
                data-testid={`nav-${cat.id}`}
                onClick={() => scrollTo(cat.id)}
                style={{
                  fontFamily: SERIF, fontSize: "1rem",
                  borderBottomColor: activeSection === cat.id ? RED : "transparent",
                  color: activeSection === cat.id ? RED : "hsl(24,5%,45%)",
                }}
                className="px-4 py-3 whitespace-nowrap border-b-2 transition-all font-medium text-sm">
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* ── FULL MENU SECTIONS ── */}
      <main className="max-w-4xl mx-auto pb-16">
        {loading ? (
          <div className="flex items-center justify-center py-24 gap-3">
            <div className="w-7 h-7 rounded-full border-2 border-t-transparent animate-spin"
              style={{ borderColor: RED, borderTopColor: "transparent" }} />
            <span className="text-sm text-muted-foreground">Loading…</span>
          </div>
        ) : (
          menu.map(({ category: cat, items }) => (
            <section key={cat.id} id={`section-${cat.id}`}
              data-catid={cat.id}
              data-testid={`section-${cat.id}`}
              ref={el => (sectionRefs.current[cat.id] = el)}
              className="mt-10 px-4 sm:px-0">

              {/* ── FOOD PHOTO (clean, no overlay text) ── */}
              <div className="rounded-2xl overflow-hidden mb-6 shadow-md"
                style={{ height: "220px", background: "#f5efe8" }}>
                <img
                  src={FOOD_PHOTOS[cat.id] || "/menu_cover.png"}
                  alt={cat.name}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "center center" }}
                  onError={e => { e.target.src = "/menu_cover.png"; }}
                />
              </div>

              {/* ── CATEGORY TITLE (below photo, not over it) ── */}
              <div className="flex items-center gap-4 mb-4 px-1">
                <div>
                  <h2 style={{ fontFamily: SERIF, color: RED, fontSize: "2.2rem", fontWeight: 400, fontStyle: "italic", lineHeight: 1.1 }}>
                    {cat.name}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-0.5 tracking-wider uppercase"
                    style={{ letterSpacing: "0.12em", fontSize: "0.7rem" }}>
                    {cat.description_en}
                  </p>
                </div>
                <div className="flex-1 h-px" style={{ background: "hsl(24,5%,88%)" }} />
              </div>

              {/* ── ITEMS ── */}
              <div className="divide-y" style={{ borderColor: "hsl(24,5%,92%)" }}>
                {items.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 px-1">—</p>
                ) : (
                  items.map(item => (
                    <div key={item.id} data-testid={`item-${item.id}`}
                      className="flex items-start justify-between gap-4 py-4 px-1">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start flex-wrap gap-1.5">
                          <span style={{ fontFamily: SERIF, fontSize: "1.25rem", fontWeight: 500, lineHeight: 1.2 }}>
                            {item.name}
                          </span>
                          {item.is_gf && (
                            <span className="mt-1 text-xs px-2 py-0.5 rounded-full font-semibold uppercase border flex-shrink-0"
                              style={{ color: "#15803d", background: "#f0fdf4", borderColor: "#bbf7d0" }}>
                              GF
                            </span>
                          )}
                        </div>
                        {item.description && (
                          <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">
                            {item.description}
                          </p>
                        )}
                      </div>
                      {item.price != null && (
                        <span className="text-sm font-semibold whitespace-nowrap flex-shrink-0 tabular-nums"
                          style={{ color: RED }}>
                          {Number(item.price).toFixed(2)} €
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </section>
          ))
        )}
      </main>

      {/* ── FOOTER ── */}
      <footer className="border-t py-12 text-center px-4 mt-4"
        style={{ background: "hsl(0,68%,18%)" }}>
        <div className="text-white/90" style={{ fontFamily: SERIF, fontSize: "2rem", fontStyle: "italic", fontWeight: 300 }}>
          Bianco e Rosso
        </div>
        <p className="text-white/70 text-sm mt-2">Planos 291 00, Tsilivi, Zakynthos</p>
        <p className="text-white/70 text-sm mt-1">+30 699 612 5838 · Open daily 17:00 – 23:59</p>
        <div className="flex items-center justify-center gap-2 mt-4 text-white/50 text-xs">
          <Globe className="w-3.5 h-3.5" />
          <span>{lang.name} {lang.flag}</span>
        </div>
        <a href="https://biancoerosso.gr" target="_blank" rel="noopener noreferrer"
          className="mt-3 inline-block text-white/40 text-xs hover:text-white/70 transition-colors">
          biancoerosso.gr
        </a>
      </footer>
    </div>
  );
}
